import { useId } from "react";

function buildSmoothPath(coords: readonly (readonly [number, number])[]) {
  if (coords.length < 2) {
    const [x, y] = coords[0] ?? [0, 0];
    return `M${x.toFixed(2)} ${y.toFixed(2)}`;
  }

  return coords
    .map(([x, y], index) => {
      if (index === 0) {
        return `M${x.toFixed(2)} ${y.toFixed(2)}`;
      }

      const [prevX, prevY] = coords[index - 1];
      const [nextX, nextY] = coords[index + 1] ?? [x, y];
      const [prevPrevX, prevPrevY] = coords[index - 2] ?? [prevX, prevY];
      const control1X = prevX + (x - prevPrevX) / 6;
      const control1Y = prevY + (y - prevPrevY) / 6;
      const control2X = x - (nextX - prevX) / 6;
      const control2Y = y - (nextY - prevY) / 6;

      return `C${control1X.toFixed(2)} ${control1Y.toFixed(2)} ${control2X.toFixed(2)} ${control2Y.toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

export function Sparkline({
  points,
  tone = "accent",
}: {
  points: number[];
  tone?: "accent" | "success" | "warning" | "danger" | "ember" | "info";
}) {
  const gradientId = `spark-${useId().replace(/:/g, "")}`;
  const safePoints = points.length ? points : [0];
  const max = Math.max(...safePoints, 1);
  const min = Math.min(...safePoints, 0);
  const range = Math.max(max - min, 1);
  const width = 128;
  const height = 34;
  const step = safePoints.length > 1 ? width / (safePoints.length - 1) : width;
  const coords = safePoints.map((point, index) => {
    const x = index * step;
    const y = height - 4 - ((point - min) / range) * (height - 10);
    return [x, y] as const;
  });
  const line = buildSmoothPath(coords);
  const area = `${line} L${width} ${height} L0 ${height} Z`;
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "danger"
          ? "text-danger"
          : tone === "ember"
            ? "text-ember-500"
            : tone === "info"
              ? "text-info"
              : "text-accent-600";
  const lastPoint = coords[coords.length - 1] ?? [width, height / 2];

  return (
    <svg aria-hidden="true" className={`h-9 w-32 ${toneClass}`} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M0 ${height - 5}H${width}`} stroke="currentColor" strokeWidth="1" opacity="0.14" />
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={`M${lastPoint[0].toFixed(2)} 4V${height - 4}`}
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.18"
      />
      <path
        className="sparkline-path"
        d={line}
        fill="none"
        pathLength={1}
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.5"
      />
      <circle cx={lastPoint[0]} cy={lastPoint[1]} fill="var(--color-paper)" r="3.5" />
      <circle cx={lastPoint[0]} cy={lastPoint[1]} fill="currentColor" r="2" />
    </svg>
  );
}
